<template>
  <!-- Boards Details View -->
  <div class="w-full h-full">
    <!-- Top CTA Nav -->
    <div class="flex justify-between items-center mx-4 pt-4">
      <!-- Logo -->
      <div class="flex items-center select-none">
        <img
          src="../../assets/images/brief-foreplay-icon.svg"
          alt=""
          class="filter invert h-8"
        >
        <div
          class="border border-white border-opacity-20 bg-white bg-opacity-10 rounded-md flex - items-center justify-center px-1.5"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-text-disabled font-medium -mt-0.5"
          >
            experts
          </BaseText>
        </div>
      </div>
      <!-- Buttons -->
      <div class="flex items-center">
        <div class="hidden md:flex lg:flex">
          <BaseButton
            class="text-white"
            @click="$router.push({ name: 'LoginView' })"
          >
            Log In
          </BaseButton>
        </div>
        <BaseButton
          primary
          @click="$router.push({ name: 'SignUpView' })"
        >
          Sign Up
        </BaseButton>
      </div>
    </div>

    <!-- Main Library Section -->
    <MainLayout
      v-scroll="onScroll"
      scroll-toolbar
      :aditional-classes="'ml-4'"
      style="height: calc(100vh - 90px); width: calc(100vw - 30px)"
    >
      <!-- Toolbar -->
      <template #toolbar>
        <!-- Filters -->
        <div class="flex px-2 justify-between items-center">
          <!-- Profile Section -->
          <div class="flex flex-col">
            <div class="flex items-start py-2">
              <img
                class="rounded-lg h-16 w-16 mr-2"
                :src="expert.avatar"
              >
              <div>
                <div class="text-lg font-medium">
                  {{ expert.fullName }}
                </div>
                <!-- Socials -->
                <div class="flex items-center">
                  <!-- Twitter -->
                  <a
                    v-if="expert.twitter"
                    :href="expert.twitter"
                    target="_blank"
                    class="mr-2"
                  >
                    <TwitterIcon :width="22" />
                  </a>

                  <!-- Facebook -->
                  <a
                    v-if="expert.facebook"
                    :href="expert.facebook"
                    target="_blank"
                    class="mr-2"
                  >
                    <FacebookIcon :width="22" />
                  </a>

                  <!-- Instagram -->
                  <a
                    v-if="expert.instagram"
                    :href="expert.instagram"
                    target="_blank"
                    class="mr-2"
                  >
                    <InstagramIcon :width="22" />
                  </a>

                  <!-- linkedin -->
                  <a
                    v-if="expert.linkedin"
                    :href="expert.linkedin"
                    target="_blank"
                    class="mr-2"
                  >
                    <LinkedInIcon :width="22" />
                  </a>

                  <!-- YouTube -->
                  <a
                    v-if="expert.youtube"
                    :href="expert.youtube"
                    target="_blank"
                    class="mr-2"
                  >
                    <YouTubeIcon :width="22" />
                  </a>

                  <!-- Website -->
                  <a
                    v-if="expert.website"
                    :href="expert.website"
                    target="_blank"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="w-5 h-5"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #filter>
        <div ref="filterBar" class="border border-border-normal bg-white bg-opacity-90" style="backdrop-filter: blur(6px)">
          <FilterTopBar
            :active-filters.sync="activeFilters"
            :allowed-filter-types="['niche', 'format', 'status', 'platform', 'language']"
            :sort-order.sync="sortOrder"
            :text-search.sync="textSearch"
            hide-saved-filters
            @change="fetchAdvertisements"
          />
        </div>
      </template>

      <template #content>
        <!-- Side Scrollable Boards -->
        <div
          class="w-full overflow-x-scroll scrollbar-hide h-10 flex justify-start items-center px-4 -mt-2 mb-2"
        >
          <!-- Boards Title -->
          <div class="select-none text-gray-600 text-md font-medium mr-2">
            Boards
          </div>

          <!-- Ad Saved Count -->
          <div
            :class="
              !selectedBoard.id
                ? 'bg-purple-200 text-purple-600'
                : 'bg-gray-200 text-gray-600 hover:bg-purple-100 hover:text-purple-600'
            "
            class="bg-gray-200 rounded px-2 py-1 select-none flex items-center cursor-pointer whitespace-nowrap"
            @click="setSelectedBoard(null)"
          >
            All Ads
          </div>

          <!-- Boards -->
          <div
            v-for="(board, index) in boards"
            :key="index"
            class="rounded px-2 py-1 select-none cursor-pointer transition duration-200 ml-2 whitespace-nowrap group"
            :class="
              selectedBoard.id === board.id
                ? 'bg-purple-200 text-purple-600'
                : 'bg-gray-200 hover:bg-purple-100 hover:text-purple-600 text-text-muted'
            "
            @click="setSelectedBoard(board.id)"
          >
            <div
              v-if="board.name"
              class="flex items-center"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="mr-1 transition duration-200"
                :class="
                  selectedBoard.id === board.id
                    ? 'bg-purple-200 text-purple-600'
                    : 'bg-gray-200 group-hover:bg-purple-100 group-hover:text-purple-600 text-text-normal'
                "
              >
                <path
                  d="M4.03502 16.6933L4.37551 16.0251H4.37551L4.03502 16.6933ZM3.30666 15.965L3.97491 15.6245H3.97491L3.30666 15.965ZM16.6933 15.965L16.0251 15.6245L16.0251 15.6245L16.6933 15.965ZM15.965 16.6933L15.6245 16.0251L15.6245 16.0251L15.965 16.6933ZM16.6933 4.03502L16.0251 4.37551L16.0251 4.37551L16.6933 4.03502ZM15.965 3.30666L15.6245 3.97491L15.6245 3.97491L15.965 3.30666ZM3.30666 4.03502L2.6384 3.69452L2.6384 3.69452L3.30666 4.03502ZM4.03502 3.30666L3.69452 2.6384L3.69452 2.6384L4.03502 3.30666ZM10.75 3.125V2.375H9.25V3.125H10.75ZM3.125 7.375H2.375V8.875H3.125V7.375ZM9.25 16.875V17.625H10.75V16.875H9.25ZM16.875 12.625H17.625V11.125H16.875V12.625ZM16.125 5.79167V14.2083H17.625V5.79167H16.125ZM14.2083 16.125H5.79167V17.625H14.2083V16.125ZM3.875 14.2083V5.79167H2.375V14.2083H3.875ZM5.79167 3.875H14.2083V2.375H5.79167V3.875ZM5.79167 16.125C5.31258 16.125 4.99837 16.1244 4.75808 16.1048C4.52654 16.0859 4.43042 16.0531 4.37551 16.0251L3.69452 17.3616C3.99613 17.5153 4.31163 17.5733 4.63593 17.5998C4.95147 17.6256 5.33733 17.625 5.79167 17.625V16.125ZM2.375 14.2083C2.375 14.6627 2.37442 15.0485 2.4002 15.3641C2.42669 15.6884 2.48472 16.0039 2.6384 16.3055L3.97491 15.6245C3.94693 15.5696 3.91413 15.4735 3.89522 15.2419C3.87558 15.0016 3.875 14.6874 3.875 14.2083H2.375ZM4.37551 16.0251C4.20303 15.9372 4.0628 15.797 3.97491 15.6245L2.6384 16.3055C2.87009 16.7602 3.2398 17.1299 3.69452 17.3616L4.37551 16.0251ZM16.125 14.2083C16.125 14.6874 16.1244 15.0016 16.1048 15.2419C16.0859 15.4735 16.0531 15.5696 16.0251 15.6245L17.3616 16.3055C17.5153 16.0039 17.5733 15.6884 17.5998 15.3641C17.6256 15.0485 17.625 14.6627 17.625 14.2083H16.125ZM14.2083 17.625C14.6627 17.625 15.0485 17.6256 15.3641 17.5998C15.6884 17.5733 16.0039 17.5153 16.3055 17.3616L15.6245 16.0251C15.5696 16.0531 15.4735 16.0859 15.2419 16.1048C15.0016 16.1244 14.6874 16.125 14.2083 16.125V17.625ZM16.0251 15.6245C15.9372 15.797 15.797 15.9372 15.6245 16.0251L16.3055 17.3616C16.7602 17.1299 17.1299 16.7602 17.3616 16.3055L16.0251 15.6245ZM17.625 5.79167C17.625 5.33733 17.6256 4.95147 17.5998 4.63593C17.5733 4.31163 17.5153 3.99613 17.3616 3.69452L16.0251 4.37551C16.0531 4.43042 16.0859 4.52654 16.1048 4.75808C16.1244 4.99837 16.125 5.31258 16.125 5.79167H17.625ZM14.2083 3.875C14.6874 3.875 15.0016 3.87558 15.2419 3.89522C15.4735 3.91413 15.5696 3.94693 15.6245 3.97491L16.3055 2.6384C16.0039 2.48472 15.6884 2.42669 15.3641 2.4002C15.0485 2.37442 14.6627 2.375 14.2083 2.375V3.875ZM17.3616 3.69452C17.1299 3.2398 16.7602 2.87009 16.3055 2.6384L15.6245 3.97491C15.797 4.0628 15.9372 4.20303 16.0251 4.37551L17.3616 3.69452ZM3.875 5.79167C3.875 5.31258 3.87558 4.99837 3.89522 4.75808C3.91413 4.52654 3.94693 4.43042 3.97491 4.37551L2.6384 3.69452C2.48472 3.99613 2.42669 4.31163 2.4002 4.63593C2.37442 4.95147 2.375 5.33733 2.375 5.79167H3.875ZM5.79167 2.375C5.33733 2.375 4.95147 2.37442 4.63593 2.4002C4.31163 2.42669 3.99613 2.48472 3.69452 2.6384L4.37551 3.97491C4.43042 3.94693 4.52654 3.91413 4.75808 3.89522C4.99837 3.87558 5.31258 3.875 5.79167 3.875V2.375ZM3.97491 4.37551C4.06279 4.20303 4.20303 4.06279 4.37551 3.97491L3.69452 2.6384C3.2398 2.87009 2.87009 3.2398 2.6384 3.69452L3.97491 4.37551ZM9.25 3.125V8.125H10.75V3.125H9.25ZM10 7.375H3.125V8.875H10V7.375ZM9.25 8.125V11.875H10.75V8.125H9.25ZM9.25 11.875V16.875H10.75V11.875H9.25ZM10 12.625H16.875V11.125H10V12.625Z"
                  fill="currentColor"
                />
              </svg>
              {{ board.name.substring(1, board.name.length) }}
            </div>
          </div>
        </div>
        <!-- Loading State -->
        <BaseLoadingSpinner
          v-if="loadingAdvertisements && !loadingAdvertisements"
          class="mt-24"
        />

        <!-- Ads List -->
        <AdvertisementList
          v-else
          :advertisements="advertisements"
          :shared-board="true"
          discovery
          @getMoreAds="() => getMoreAds({})"
          @reload="fetchAdvertisements(false, true)"
        />

        <infinite-loading
          v-if="advertisements.length"
          :identifier="infiniteId"
          @infinite="getMoreAds"
        >
          <div slot="spinner">
            <BaseLoadingLogo :margin="2" />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </template>
    </MainLayout>

    <DiscoveryExpertsCard v-if="advertisements.length" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import firebase from '@/api/config/FirebaseConfig'
import ForeplayAPI from '@/api/foreplayServer'
import AdvertisementList from '../../components/advertisements/AdvertisementList.vue'
import MainLayout from '../../layouts/MainLayout.vue'
import InfiniteLoading from 'vue-infinite-loading'
import Logo from '../../components/globals/Logo.vue'

import DiscoveryExpertsCard from '../../components/discovery/DiscoveryExpertsCard.vue'
import FilterTopBar from '../../components/search/FilterTopBar.vue'

import FacebookIcon from '../../components/globals/Icons/FilterIcons/FacebookIcon.vue'
import InstagramIcon from '../../components/globals/Icons/FilterIcons/InstagramIcon.vue'
import LinkedInIcon from '../../components/globals/Icons/FilterIcons/LinkedInIcon.vue'
import YouTubeIcon from '../../components/globals/Icons/FilterIcons/YouTubeIcon.vue'
import TwitterIcon from '../../components/globals/Icons/FilterIcons/TwitterIcon.vue'

export default {
  name: 'DiscoveryExpertView',
  components: {
    AdvertisementList,
    DiscoveryExpertsCard,
    Logo,
    MainLayout,
    InfiniteLoading,
    FilterTopBar,
    InstagramIcon,
    FacebookIcon,
    LinkedInIcon,
    TwitterIcon,
    YouTubeIcon
  },
  data () {
    return {
      activeFilters: {},
      expert: {},
      folder: {},
      selectedBoard: {},
      sortOrder: [{ name: 'Newest', value: 'desc' }],
      docOrder: 'desc',
      textSearch: '',
      advertisements: [],
      boards: [],
      loadingAdvertisements: false,
      infiniteId: +new Date(),
      lastDocId: null
    }
  },
  computed: {
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('AuthModule', ['getUserEmail', 'getUserName', 'getTeam']),
    ...mapGetters('BriefsModule', ['getIsInspoMode']),
    ...mapGetters('BoardsModule', ['getBoards']),
    getShareLink () {
      if (this.selectedBoard.id) {
        return (
          process.env.VUE_APP_BASE_URL +
          '/share/discovery-experts/' +
          encodeURIComponent(this.$route.params.id) +
          '/board/' +
          this.selectedBoard.id +
          '?user=' +
          encodeURIComponent(this.getUserName)
        )
      }

      return (
        process.env.VUE_APP_BASE_URL +
        '/share/discovery-experts/' +
        encodeURIComponent(this.$route.params.id) +
        '?user=' +
        encodeURIComponent(this.getUserName)
      )
    }
  },
  async mounted () {
    try {
      this.fetchBoards()
    } catch (error) {
      console.log('no auth')
    }
    // Set initial top bar styles
    this.$refs.filterBar.style.position = 'sticky'
    this.$refs.filterBar.style.top = '16px'
    this.$refs.filterBar.style.zIndex = '50000'

    this.fetchNiches()
    await this.fetchExpert()
    await this.fetchAdvertisements()
  },
  methods: {
    ...mapActions('BoardsModule', ['fetchBoards']),
    onScroll (e, position) {
      const marginValue =
        position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding =
        position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5

      this.$refs.filterBar.style.marginLeft = `${marginValue}px`
      this.$refs.filterBar.style.marginRight = `${marginValue}px`
      this.$refs.filterBar.style.marginTop = `${marginValue}px`
      this.$refs.filterBar.style.borderRadius = `${rounding}px`

      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue
          ? position.scrollTop / 15
          : maxValue
      }

      if (position.scrollTop) {
        this.$refs.filterBar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          15
        )}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          7
        )}px ${shadowSection(
          10
        )}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(
          7
        )}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(
          3
        )}px ${shadowSection(5)}px`
        // this.$refs.filterBar.style.boxShadow = `rgba(58, 111, 251, 0.4) -${shadowSection(5)}px ${shadowSection(5)}px, rgba(58, 111, 251, 0.3) -${shadowSection(10)}px ${shadowSection(10)}px, rgba(58, 111, 251, 0.2) -${shadowSection(15)}px ${shadowSection(15)}px, rgba(58, 111, 251, 0.1) -${shadowSection(20)}px ${shadowSection(20)}px, rgba(58, 111, 251, 0.05) -${shadowSection(25)}px ${shadowSection(25)}px`
      } else {
        this.$refs.filterBar.style.boxShadow = null
      }
    },
    async fetchNiches () {
      // Setup
      const db = firebase.firestore()

      const snapshot = await db.collection('niches').get()

      const newDocs = snapshot.docs?.map((doc) => {
        const docData = {
          ...(doc.data() || [])
        }

        return { name: docData.niche, icon: docData.emoji }
      })

      const sortedDocs = newDocs.sort((a, b) => (a.name > b.name ? 1 : -1))
      const otherIndex = sortedDocs.findIndex((d) => d.name === 'Other')
      const otherNiche = sortedDocs.splice(otherIndex, 1)

      sortedDocs.push(otherNiche[0])

      this.niches = sortedDocs
    },
    handleClipboardCopy () {
      this.$showAlert({
        message: 'Copied share link to clipboard!',
        type: 'success'
      })
    },
    setSelectedBoard (boardId, skipPathUpdate) {
      if (this.selectedBoard.id === boardId) return

      let pathToPush
      if (!boardId) {
        if (!this.selectedBoard.id) return
        this.selectedBoard = {}

        pathToPush = this.$route.path
      } else {
        this.selectedBoard = this.boards.find((b) => b.id === boardId)

        pathToPush =
          this.$route.path + '/' + 'board/' + encodeURIComponent(boardId)
      }

      // Update history
      if (!skipPathUpdate) {
        history.pushState({}, null, pathToPush)
      }

      // Fetch Ads now from the selected boards
      this.fetchAdvertisements()
    },
    async fetchExpert () {
      try {
        const response = await ForeplayAPI.Users.getExpertUserByUTM(this.$route.params.id)
        const { error, data, message } = response || {}

        if (error || !data?.expert) {
          this.$showAlert({
            message: message || 'Unable to load this expert',
            type: 'error'
          })
          return
        }

        this.expert = data.expert
        this.boards = data.boards || []

        if (this.$route.params.boardId) {
          this.setSelectedBoard(this.$route.params.boardId, true)
        }

        const referralLink = data.referralLink
        const rewardfulUTM = referralLink ? referralLink.split('?via=')[1] : ''
        if (rewardfulUTM) {
          // eslint-disable-next-line no-undef
          rewardful('source', rewardfulUTM)
        }
      } catch (e) {
        console.error(e)
        this.$showAlert({
          message: e.response?.data?.message || e.message || 'Unable to load this expert',
          type: 'error'
        })
      }
    },
    // Fetch Advertisements
    async getMoreAds ($state) {
      const filters = {
        ...this.activeFilters,
        textSearch: this.textSearch,
        sort: this.sortOrder[0].value
      }

      if (this.selectedBoard.id) {
        filters.orBoardId = [this.selectedBoard.id]
      }

      const { results, nextPage } = await ForeplayAPI.Ads.getExpertAds(
        this.$route.params.id,
        this.lastDocId,
        filters
      )

      this.lastDocId = nextPage

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      this.advertisements = this.advertisements.concat(results)
      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    // Fetch Advertisements
    async fetchAdvertisements (load = true, refresh = false) {
      this.loadingAdvertisements = load
      this.advertisements = []
      if (refresh) {
        this.lastDocId = null
      }

      try {
        const filters = {
          ...this.activeFilters,
          textSearch: this.textSearch,
          sort: this.sortOrder[0].value
        }

        if (this.selectedBoard.id) {
          filters.orBoardId = [this.selectedBoard.id]
        }

        const { results, nextPage } = await ForeplayAPI.Ads.getExpertAds(
          this.$route.params.id,
          null,
          filters
        )

        this.lastDocId = nextPage

        this.advertisements = results
      } catch (e) {
        console.log(e)
        this.$showAlert({
          message: e,
          type: 'error'
        })
      } finally {
        this.loadingAdvertisements = false
        this.infiniteId += 1

        setTimeout(() => {
          window.dispatchEvent(new Event('resize'))
        }, 100)
      }
    }
  }
}
</script>

<style scoped lang="sass"></style>
